/**
 * `WizardQuestionFlow` — the per-field question machine for a selected topic
 * (027 US6 PR-2, T060/T061; FR-024/FR-026/FR-027; SC-010).
 *
 * The step sequence is a VIEW over the UNCHANGED engine: the active path
 * comes from `resolveActivePath(topic.fields, values)` — one step per visible
 * field, in path order (a reveal-controller's follow-ups insert right after
 * it). Changing an earlier answer re-resolves the path forward; answers on an
 * abandoned branch stay in the values map but are FILTERED OUT of the routed
 * payload by the same visible-field filter the inline renderer shipped, so
 * nothing the member backed out of reaches PPJ.
 *
 * Phases:
 *   field(index) → … → attachments? → confirm     (the happy path)
 *   field(index) ──answer triggers a dead-end──▶ dead-end(from: index)
 *
 *   - A guidance dead-end (`send: false`) is TERMINAL: catalogued text +
 *     external link, no Seuraava, no Lähetä anywhere on the path (FR-027);
 *     back returns to the controller step.
 *   - The attachment step exists only when the catalogue allows attachments
 *     for the topic (FR-011 unchanged).
 *   - `confirm` is the "Vahvista tiedot" overview (T062/T063, FR-028):
 *     summary rows + the Lakikaverille PREFIX/SUFFIX display slots
 *     (`ConfirmationStep.tsx`), Lähetä pinned in the bottom bar. Submitting
 *     reuses the shipped machinery (`useComposeSubmit`; the FR-015/FR-016
 *     copy-authority rules): success swaps the body for the thank-you
 *     terminal (FR-029, back hidden, Sulje pinned); a failure renders its
 *     notice ON the overview so the answers stay reviewable and re-sendable
 *     (SC-004 — no dead ends).
 *
 * Layout (the owner fold at PR-3): one pinned `WizardTopBar`, ONE persistent
 * `WizardScrollArea` holding the phase body, and the phase's action bar
 * pinned BELOW the scroll area. The flow owns its scroll container and
 * resets its position on every internal transition (advance, back, the
 * attachment skip) — the outer wizard step stays 'questions' throughout, so
 * the shell's own scroll reset never fires here. Gating moved with the
 * pinned bar: the flow computes per-step `Seuraava` enablement (including
 * the date parse — the draft `pp.kk.vvvv` text lives here, NOT in the step
 * body) so the bodies stay presentation-only.
 *
 * Dirty signal (FR-025, the review's LOW-3 tightening): the parent's abandon-
 * confirm keys on "≥1 answered field" — reported event-driven through
 * `onDirtyChange` on every value/attachment change, and reset to false after
 * a successful send (nothing unsaved remains to discard).
 */

import { type JSX, useContext, useEffect, useRef, useState } from 'react';

import type { MessageKeys } from '../../../i18n/messages/index.js';
import { useTranslate } from '../../../i18n/useTranslate.js';
import { invalidateCasesCache } from '../../../member-cases/useCases.js';
import { Dialog } from '../../../primitives/Dialog.js';
import { PortsContext } from '../../../providers/PortsContext.js';
import type { ComposeAttachment } from '../attachmentRef.js';
import type { Field, TopicDefinition } from '../MessageComposePort.js';
import { resolveActivePath } from '../revealResolution.js';
import { useComposeAttachmentUpload } from '../useComposeAttachmentUpload.js';
import { useComposeSubmit } from '../useComposeSubmit.js';
import { ConfirmationOverview, ThankYouStep } from './ConfirmationStep.js';
import {
  type ProgressDotsState,
  stepColumnClasses,
  stepLeadClasses,
  stepNoticeClasses,
  stepNoticeWrapClasses,
  WizardBottomBar,
  WizardScrollArea,
  WizardTopBar,
} from './chrome.js';
import { formatIsoToFinnish, parseFinnishDateToIso } from './dateText.js';
import { AttachmentStep, ChoiceStep, DateStep, TextStep } from './QuestionStepBodies.js';
import { handleWizardAdvanceKey } from './wizardKeyboard.js';

type FlowPhase =
  | { readonly kind: 'field'; readonly index: number }
  | { readonly kind: 'attachments' }
  | { readonly kind: 'confirm' }
  | { readonly kind: 'dead-end'; readonly from: number };

/** The CURRENT date step's keystrokes (`pp.kk.vvvv`), keyed to its field. */
interface DateDraft {
  readonly field: string;
  readonly text: string;
}

const guidanceLinkClasses = 'font-medium text-primary underline';

export interface WizardQuestionFlowProps {
  readonly topic: TopicDefinition;
  /** Back past the first field: return to the wizard's topic step. */
  readonly onExitToTopics: () => void;
  /** Close request after a successful send (no data left to discard). */
  readonly requestClose: () => void;
  /** FR-025 dirty signal: true once ≥1 field is answered (LOW-3 reading). */
  readonly onDirtyChange: (dirty: boolean) => void;
}

function isDirty(
  values: Record<string, string>,
  attachments: readonly ComposeAttachment[],
): boolean {
  return attachments.length > 0 || Object.values(values).some((value) => value.trim().length > 0);
}

/** Missing required field names among the VISIBLE fields (engine semantics). */
function missingRequired(
  visibleFields: readonly Field[],
  values: Readonly<Record<string, string>>,
): number {
  let missing = 0;
  for (const field of visibleFields) {
    if (field.required && (values[field.name] ?? '').trim().length === 0) {
      missing += 1;
    }
  }
  return missing;
}

/**
 * r1 item 7b (D-123): the active per-option attachment hint, if any. Scans the
 * VISIBLE fields for a radio/select whose selected option carries an
 * `attachmentHint` (the termination grounds, FR-033), and returns its key. The
 * reveal-resolution engine is unchanged — this reads the already-resolved
 * active path. Undefined → the attachment step falls back to the topic hint.
 */
function activeOptionAttachmentHint(
  visibleFields: readonly Field[],
  values: Readonly<Record<string, string>>,
): MessageKeys | undefined {
  for (const field of visibleFields) {
    const selected = values[field.name];
    if (selected === undefined) {
      continue;
    }
    const option = field.options?.find((o) => o.value === selected);
    if (option?.attachmentHint !== undefined) {
      return option.attachmentHint;
    }
  }
  return undefined;
}

export function WizardQuestionFlow({
  topic,
  onExitToTopics,
  requestClose,
  onDirtyChange,
}: WizardQuestionFlowProps): JSX.Element {
  const t = useTranslate();
  const ports = useContext(PortsContext);
  const { submit, state, result, failureKind } = useComposeSubmit();
  const {
    uploadAll,
    phase: attachmentPhase,
    results: attachmentResults,
  } = useComposeAttachmentUpload();
  const [values, setValues] = useState<Record<string, string>>({});
  const [attachments, setAttachments] = useState<readonly ComposeAttachment[]>([]);
  const [phase, setPhase] = useState<FlowPhase>({ kind: 'field', index: 0 });
  const [dateDraft, setDateDraft] = useState<DateDraft | null>(null);
  // The flow's ONE scrolling region (the owner fold): position resets on
  // every internal transition; the node persists across them, so the reset
  // targets a live element rather than a remounting one.
  const scrollAreaRef = useRef<HTMLDivElement>(null);

  const resetScroll = (): void => {
    scrollAreaRef.current?.scrollTo({ top: 0 });
  };

  const hasAttachmentStep = topic.attachments.kind === 'optional';
  const path = resolveActivePath(topic.fields, values);
  const totalQuestionSteps = path.visibleFields.length + (hasAttachmentStep ? 1 : 0);

  const updateValue = (fieldName: string, value: string): void => {
    const nextValues = { ...values, [fieldName]: value };
    setValues(nextValues);
    onDirtyChange(isDirty(nextValues, attachments));
  };

  const updateAttachments = (next: readonly ComposeAttachment[]): void => {
    setAttachments(next);
    onDirtyChange(isDirty(values, next));
  };

  /** Advance from field `index` against `vals` (the just-applied values). */
  const advanceFrom = (index: number, vals: Record<string, string>): void => {
    resetScroll();
    const nextPath = resolveActivePath(topic.fields, vals);
    if (nextPath.deadEnd !== null) {
      setPhase({ kind: 'dead-end', from: index });
      return;
    }
    if (index + 1 < nextPath.visibleFields.length) {
      setPhase({ kind: 'field', index: index + 1 });
      return;
    }
    setPhase(hasAttachmentStep ? { kind: 'attachments' } : { kind: 'confirm' });
  };

  const goBack = (): void => {
    resetScroll();
    switch (phase.kind) {
      case 'field':
        if (phase.index === 0) {
          onExitToTopics();
          return;
        }
        setPhase({ kind: 'field', index: phase.index - 1 });
        return;
      case 'attachments':
        setPhase({ kind: 'field', index: path.visibleFields.length - 1 });
        return;
      case 'confirm':
        setPhase(
          hasAttachmentStep
            ? { kind: 'attachments' }
            : { kind: 'field', index: path.visibleFields.length - 1 },
        );
        return;
      case 'dead-end':
        setPhase({ kind: 'field', index: phase.from });
        return;
    }
  };

  const handleSend = (): void => {
    if (state === 'submitting' || attachmentPhase === 'uploading') {
      return;
    }
    const sendPath = resolveActivePath(topic.fields, values);
    if (sendPath.deadEnd !== null || missingRequired(sendPath.visibleFields, values) > 0) {
      return;
    }
    // The same visible-field payload filter the inline renderer shipped (F4):
    // abandoned-branch answers never reach the routed payload.
    const allowed = new Set(sendPath.visibleFields.map((field) => field.name));
    const topicValues = Object.fromEntries(
      Object.entries(values).filter(([fieldName]) => allowed.has(fieldName)),
    );
    void (async () => {
      const sent = await submit({
        serviceLine: topic.serviceLine,
        topicKey: topic.key,
        values: topicValues,
        // Metadata-only on the wire; the real bytes upload after the case is
        // created (051). The submit payload never carries File bytes.
        attachments: attachments.map((attachment) => attachment.ref),
      });
      if (sent?.outcome === 'success') {
        // 052 US1: the send created a real case — evict the member-cases list
        // cache so the member's NEXT view of the Viestit list is a fresh
        // route-on-read that includes it (never a fabricated row; a FAILED send
        // does not reach here, so nothing is evicted on failure — FR-002/P7).
        // The consequent re-read of the still-mounted list is made concurrent by
        // the transition-wrapped close at `ComposeWizard`'s `resetAndClose` seam,
        // so the list is not torn down to its Suspense fallback. ORDERING NOTE:
        // eviction is eager (here, on
        // success — before `uploadAll` and before close), so the FR-004 no-re-
        // suspend guard assumes the mounted `CasesList` does not re-render in the
        // gap between this eviction and the transition-wrapped close; it does not
        // in the current `Messages`/overlay layout (nothing re-renders it during
        // the pending window). A layout that DID re-render it in that gap would
        // need its own transition on that re-read.
        if (ports?.memberCases !== undefined) {
          invalidateCasesCache(ports.memberCases);
        }
        // 051 US1: transfer each attachment's real bytes to the created case
        // (reusing the member-cases upload path). Partial failures are surfaced
        // honestly on the thank-you view (US2).
        if (attachments.length > 0) {
          await uploadAll(sent.externalRef ?? '', attachments);
        }
      }
      // Success or failure, the answers were either routed or preserved on
      // screen; only a SUCCESS leaves nothing to discard on close.
    })();
  };

  const sentSuccessfully = result?.outcome === 'success';
  // 052 US3: the attachment-upload pending window, computed at wizard scope so
  // the persistent live region below can announce it. True from the success
  // commit until the upload pass reports `done`; drives the hoisted `aria-live`
  // region that pre-exists (empty) across confirm→pending→result — the reliable
  // announcement the note inside ThankYouStep no longer carries.
  const attachmentUploadPending =
    sentSuccessfully && attachments.length > 0 && attachmentPhase !== 'done';
  // 052 US1/FR-004: the transition that keeps the still-mounted `CasesList` from
  // re-suspending on close now lives at the SINGLE close seam (`ComposeWizard`'s
  // `resetAndClose`), so EVERY close path is covered — including Escape, which
  // never routed through this component (backdrop/outside clicks are
  // intentionally non-dismissible). `requestClose` is used directly here.
  // After a successful send nothing unsaved remains, so closing must not
  // raise the abandon-confirm. An Effect (not a render-phase call): the
  // signal targets the PARENT's state and fires once per success commit.
  useEffect(() => {
    if (sentSuccessfully) {
      onDirtyChange(false);
    }
  }, [sentSuccessfully, onDirtyChange]);

  const dotsFor = (current: number): ProgressDotsState => ({
    total: totalQuestionSteps,
    current,
  });

  // ----- per-phase body + pinned chrome -----

  let onBack: (() => void) | undefined = goBack;
  let body: JSX.Element | null = null;
  let footer: JSX.Element | null = null;
  // The step's "Seuraava" intent, non-null only where the pinned button is
  // shown AND enabled; `handleWizardAdvanceKey` (wizardKeyboard.ts) owns the
  // Enter policy. Never set on confirm/terminal phases — their bars take
  // focus instead (Enter activates the button natively).
  let advanceIntent: (() => void) | null = null;

  if (phase.kind === 'field') {
    // Clamp instead of a render-phase setState recovery (review finding LOW-1): if an
    // earlier answer shrank the path under a stale index, render the last
    // field of the new path; the next interaction proceeds from there.
    const safeIndex = Math.min(phase.index, Math.max(0, path.visibleFields.length - 1));
    const field = path.visibleFields[safeIndex];
    if (field === undefined) {
      // An empty path cannot occur for a catalogued topic (every topic has
      // fields); render just the chrome as the safe degenerate state.
      body = null;
    } else if (field.type === 'radio' || field.type === 'select') {
      body = (
        <ChoiceStep
          key={field.name}
          field={field}
          selectedValue={values[field.name] ?? ''}
          onPick={(name, value) => {
            const nextValues = { ...values, [name]: value };
            setValues(nextValues);
            onDirtyChange(isDirty(nextValues, attachments));
            advanceFrom(safeIndex, nextValues);
          }}
        />
      );
    } else if (field.type === 'date') {
      // The draft keystrokes live HERE so the parse can gate the pinned
      // Seuraava (FR-026): only a VALID parse is committed to the values map
      // (as ISO) — the engine never sees a raw Finnish string.
      const text =
        dateDraft?.field === field.name
          ? dateDraft.text
          : formatIsoToFinnish(values[field.name] ?? '');
      const trimmed = text.trim();
      const parsed = parseFinnishDateToIso(trimmed);
      const invalid = trimmed.length > 0 && parsed === null;
      const canAdvance = trimmed.length === 0 ? !field.required : parsed !== null;
      body = (
        <DateStep
          key={field.name}
          field={field}
          text={text}
          selectedIso={parsed ?? ''}
          invalid={invalid}
          onTextChange={(nextText) => {
            setDateDraft({ field: field.name, text: nextText });
            updateValue(field.name, parseFinnishDateToIso(nextText.trim()) ?? '');
          }}
          onPick={(iso) => {
            setDateDraft({ field: field.name, text: formatIsoToFinnish(iso) });
            updateValue(field.name, iso);
          }}
        />
      );
      advanceIntent = canAdvance ? () => advanceFrom(safeIndex, values) : null;
      footer = (
        <WizardBottomBar
          label={t('compose.wizard.next')}
          disabled={!canAdvance}
          onClick={() => advanceFrom(safeIndex, values)}
          dots={dotsFor(safeIndex)}
        />
      );
    } else {
      const value = values[field.name] ?? '';
      const canAdvance = field.required ? value.trim().length > 0 : true;
      body = <TextStep key={field.name} field={field} value={value} onChange={updateValue} />;
      advanceIntent = canAdvance ? () => advanceFrom(safeIndex, values) : null;
      footer = (
        <WizardBottomBar
          label={t('compose.wizard.next')}
          disabled={!canAdvance}
          onClick={() => advanceFrom(safeIndex, values)}
          dots={dotsFor(safeIndex)}
        />
      );
    }
    // Catalogued topic INTRO (jäsenpalvelu notices, D-117 content): the proto
    // offers no slot, so the wizard carries it on the topic's FIRST question
    // step — guidance still precedes input, exactly the inline renderer's
    // contract (an unledgered display slot; flagged for owner review at
    // build PR-3, like the prefix placement was).
    if (safeIndex === 0 && topic.intro !== undefined && body !== null) {
      // One m-auto GROUP so the intro and its question center together in
      // the stage; the inner column's own auto margins find no free space
      // inside this content-sized wrapper.
      body = (
        <div className="m-auto flex w-full flex-col">
          <div className={stepNoticeWrapClasses}>
            <p className={stepNoticeClasses}>{t(topic.intro)}</p>
          </div>
          {body}
        </div>
      );
    }
  } else if (phase.kind === 'attachments') {
    body = (
      <AttachmentStep
        topic={topic}
        optionHint={activeOptionAttachmentHint(path.visibleFields, values)}
        attachments={attachments}
        onChange={updateAttachments}
      />
    );
    const goToConfirm = (): void => {
      resetScroll();
      setPhase({ kind: 'confirm' });
    };
    advanceIntent = goToConfirm;
    footer = (
      <WizardBottomBar
        label={t('compose.wizard.next')}
        onClick={goToConfirm}
        dots={dotsFor(totalQuestionSteps - 1)}
      />
    );
  } else if (phase.kind === 'dead-end') {
    const deadEnd = path.deadEnd;
    body = (
      <div className={stepColumnClasses}>
        {/* terminal-guidance idiom (FR-027): catalogued text + external
            link, NO send affordance anywhere on this path. */}
        <Dialog.Title size="wizard">{t(topic.label)}</Dialog.Title>
        {deadEnd !== null ? (
          <>
            <p className={stepLeadClasses}>{t(deadEnd.text)}</p>
            <a
              className={guidanceLinkClasses}
              href={deadEnd.link}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t('compose.guidance.link')}
            </a>
          </>
        ) : null}
      </div>
    );
  } else if (sentSuccessfully && result !== undefined) {
    // ----- thank-you terminal (T063, FR-029): the FR-015 verbatim copy;
    // back hidden (visual-map top-bar rule on terminal steps), Sulje pinned
    // — the post-success close is direct (the dirty signal already reset).
    // 051: while the real attachment bytes upload to the created case, hold a
    // brief pending state (no close); when done, the per-file results render
    // honestly (US2) and Sulje is pinned.
    onBack = undefined;
    const uploadingAttachments = attachments.length > 0 && attachmentPhase !== 'done';
    const conversationHref =
      result.externalRef !== undefined && result.externalRef.length > 0
        ? `/messages/${encodeURIComponent(result.externalRef)}`
        : undefined;
    // 052 US2: on a partial-attachment failure, the recovery link (inside
    // ThankYouStep) must be reachable — so the pinned Close does NOT steal focus
    // on that path (its Enter would discard the wizard before the member reaches
    // recovery); ThankYouStep focuses the recovery link instead. All-succeeded /
    // no-attachment keeps the existing Close focus.
    const hasFailedAttachments =
      !uploadingAttachments && (attachmentResults ?? []).some((r) => r.outcome !== 'ok');
    // Suppress the Close focus ONLY when ThankYouStep will actually focus a
    // recovery link in its place — i.e. there is a failure AND a conversation to
    // link to. If a failure occurs with no `externalRef` (no recovery target),
    // keep the Close focus so terminal focus never drops to `<body>` (WCAG 2.4.3).
    const suppressCloseFocus = hasFailedAttachments && conversationHref !== undefined;
    body = (
      <ThankYouStep
        memberMessage={result.memberMessage}
        attachmentsPending={uploadingAttachments}
        attachmentResults={
          attachments.length > 0 && !uploadingAttachments ? attachmentResults : undefined
        }
        conversationHref={conversationHref}
      />
    );
    footer = uploadingAttachments ? null : (
      <WizardBottomBar
        key="thank-you-close"
        label={t('compose.wizard.close')}
        onClick={requestClose}
        focusOnMount={!suppressCloseFocus}
      />
    );
  } else {
    // ----- confirmation overview (T063, FR-028): summary rows + the
    // Lakikaverille PREFIX/SUFFIX display slots; a failed submit's notice
    // renders ON the overview (T044 copy authority) with Lähetä still
    // pinned, so the member can fix or retry (SC-004).
    body = (
      <ConfirmationOverview
        topic={topic}
        visibleFields={path.visibleFields}
        values={values}
        attachments={attachments.map((attachment) => attachment.ref)}
        failure={{ result, thrownKind: failureKind }}
      />
    );
    footer = (
      <WizardBottomBar
        key="confirm-send"
        label={t('compose.send')}
        disabled={state === 'submitting'}
        onClick={handleSend}
        focusOnMount
      />
    );
  }

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: keyboard-shortcut DELEGATION, not an interactive element — the `display: contents` wrapper is never focusable or a tab stop; Enter originates from the focused form control inside and bubbles here. Giving it an interactive role would lie to AT about a non-element.
    <div className="contents" onKeyDown={(event) => handleWizardAdvanceKey(event, advanceIntent)}>
      <WizardTopBar onBack={onBack} onClose={requestClose} />
      <WizardScrollArea ref={scrollAreaRef}>{body}</WizardScrollArea>
      {footer}
      {/* 052 US3: the persistent attachment-upload announcement region. It lives
          HERE — outside the swapping phase body — so it pre-exists (empty) across
          confirm→pending→result and only its TEXT changes when an upload starts,
          which screen readers announce reliably (a region that mounts together
          with its text does not). Visually hidden; the sighted-member pending
          note stays in ThankYouStep. `aria-live="polite"`, not `role="status"`,
          so it does not compete with the success box's status node. */}
      <p aria-live="polite" aria-atomic="true" className="sr-only">
        {attachmentUploadPending ? t('compose.attachment.uploading') : ''}
      </p>
    </div>
  );
}
